import { m, AnimatePresence } from 'framer-motion';
// @mui
import { alpha } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
// utils
import { fData } from '@/utils/formatters/format-number';
//
import { ShowIf } from '@/utils/ui/showIf';
// import { fileStatus } from '@/sections/orders/forms/order-upload-files';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import { Box, CircularProgress } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useRef } from 'react';
import { UploadProps } from '@/types/upload';
import Iconify from '../iconify';
import { varFade } from '../../utils/animate';
import FileThumbnail, { fileData } from '../file-thumbnail';
import PDFViewer from '../preview-documents/preview-documents';

// ----------------------------------------------------------------------

export default function MultiFilePreview({
  thumbnail,
  files,
  onRemove,
  onRemoveWithId,
  sx,
  showPdfPreview = false,
}: UploadProps) {
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const { spacing, ...restSx } = (sx || {}) as any;

  const handleRemove = (file: File | string) => {
    if (file && typeof file === 'object' && '_id' in file && onRemoveWithId) {
      onRemoveWithId(file);
    } else if (onRemove) {
      onRemove(file);
    }
  };

  return (
    <Stack spacing={spacing !== undefined ? spacing : 2}>
      <AnimatePresence initial={false}>
        {files?.map((file) => {
          const { key, name = '', size = 0 } = fileData(file);
          const isNotFormatFile = typeof file === 'string';
          const isPDF = file.type === 'application/pdf';

          if (thumbnail) {
            return (
              <Stack
                key={key}
                component={m.div}
                {...varFade().inUp}
                alignItems="center"
                display="inline-flex"
                justifyContent="center"
                sx={{
                  m: 0.45,
                  width: 72,
                  height: 72,
                  borderRadius: 1.25,
                  overflow: 'hidden',
                  position: 'relative',
                  border: (theme) => `solid 1px ${alpha(theme.palette.grey[500], 0.16)}`,
                  ...restSx,
                }}
              >
                <FileThumbnail
                  tooltip
                  imageView
                  file={file}
                  sx={{ position: 'absolute' }}
                  imgSx={{ position: 'absolute' }}
                />

                {(onRemove || onRemoveWithId) && (
                  <IconButton
                    size="small"
                    onClick={() => handleRemove(file)}
                    sx={{
                      p: 0.45,
                      top: 4,
                      right: 4,
                      position: 'absolute',
                      color: 'common.white',
                      bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48),
                      '&:hover': {
                        bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
                      },
                    }}
                  >
                    <Iconify icon="mingcute:close-line" width={13} />
                  </IconButton>
                )}
              </Stack>
            );
          }

          return (
            <Stack key={key} spacing={showPdfPreview && isPDF ? 2 : 0}>
              <Stack
                component={m.div}
                {...varFade().inUp}
                spacing={0.75}
                direction="row"
                alignItems="center"
                sx={{
                  my: (restSx as any)?.my !== undefined ? (restSx as any).my : 0,
                  py: (restSx as any)?.py !== undefined ? (restSx as any).py : 0.5,
                  px: (restSx as any)?.px !== undefined ? (restSx as any).px : 1,
                  borderRadius: 1,
                  border: (theme) => `solid 1px ${alpha(theme.palette.grey[500], 0.16)}`,
                  minHeight: 40,
                  ...restSx,
                }}
              >
                <FileThumbnail file={file} sx={{ width: 28, height: 28 }} />

                <ListItemText
                  primary={isNotFormatFile ? file : name}
                  secondary={isNotFormatFile ? '' : fData(size)}
                  primaryTypographyProps={{
                    variant: 'body2',
                    noWrap: true,
                  }}
                  secondaryTypographyProps={{
                    component: 'span',
                    variant: 'caption',
                  }}
                  sx={{ my: 0 }}
                />

                <Stack direction="row" spacing={0.5} alignItems="center">
                  {showPdfPreview && isPDF && (
                    <PDFViewer
                      file={file.preview}
                      fileName={file.name}
                      fileSize={file.size}
                      validatePDF={false}
                      embedded
                      containerRef={pdfContainerRef}
                    />
                  )}

                  <ShowIf condition={(file as any)?.status === 'complete'}>
                    <CloudDoneIcon fontSize="small" color="success" />
                  </ShowIf>
                  <ShowIf condition={(file as any)?.status === 'inProgress'}>
                    <Box sx={{ m: 0, position: 'relative' }}>
                      <CloudUploadIcon fontSize="small" color="info" />
                      <ShowIf condition={!!(file as any)?.progress}>
                        <CircularProgress
                          size={20}
                          variant="determinate"
                          color="secondary"
                          value={(file as any)?.progress}
                          sx={{
                            cursor: 'pointer',
                            position: 'absolute',
                            top: -6,
                            left: -6,
                            zIndex: 1,
                          }}
                        />
                      </ShowIf>
                    </Box>
                  </ShowIf>
                  <ShowIf condition={!['complete', 'inProgress'].includes((file as any)?.status)}>
                    {(onRemove || onRemoveWithId) && (
                      <IconButton size="small" onClick={() => handleRemove(file)} sx={{ p: 0.5 }}>
                        <Iconify icon="mingcute:close-line" width={16} />
                      </IconButton>
                    )}
                  </ShowIf>
                </Stack>
              </Stack>

              {showPdfPreview && isPDF && (
                <Stack
                  ref={pdfContainerRef}
                  sx={{
                    width: '100%',
                    minHeight: 180,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                  }}
                />
              )}
            </Stack>
          );
        })}
      </AnimatePresence>
    </Stack>
  );
}
